import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { Problem } from "@/features/staff/components";
import { Credits, Note, Pager, PersonName, Table, When } from "./components";
import { bucketLabels, localMoment, supportReasonLabels } from "./format";
import { PersonPicker } from "./pickers";
import { supportAdjustmentsQuery } from "./queries";

/** Every support grant and removal, newest first: who made it, for whom, what it changed, and why. */
export function SupportAdjustments({
  filter,
  onFilterChange,
  start,
  onStartChange,
}: {
  filter: { granterUserId?: string; recipientUserId?: string };
  onFilterChange: (filter: { granterUserId?: string; recipientUserId?: string }) => void;
  /** The adjustments start just older than this operation. Missing starts at the newest. */
  start: string | undefined;
  onStartChange: (start: string | undefined) => void;
}) {
  const listTop = useRef<HTMLDivElement>(null);
  const adjustments = useQuery(supportAdjustmentsQuery(filter, start));
  const rows = adjustments.data?.adjustments ?? [];
  const next = adjustments.data?.nextBeforeOperationId;
  const newest = rows[0];
  const oldest = rows.at(-1);
  const pager = {
    label:
      newest && oldest
        ? `${localMoment(oldest.recordedAt)} – ${localMoment(newest.recordedAt)}`
        : "Nothing here",
    loading: adjustments.isPlaceholderData,
    toNewest: start ? () => onStartChange(undefined) : undefined,
    toOlder: next ? () => onStartChange(next) : undefined,
    newer: false,
  };
  return (
    <div className="space-y-3">
      <div className="-ml-3 flex flex-wrap items-center gap-1">
        <PersonPicker
          label="Made by"
          anyone="Made by anyone"
          userId={filter.granterUserId}
          onChange={granterUserId => onFilterChange({ ...filter, granterUserId })}
        />
        <PersonPicker
          label="For"
          anyone="For anyone"
          userId={filter.recipientUserId}
          onChange={recipientUserId => onFilterChange({ ...filter, recipientUserId })}
        />
      </div>
      <div ref={listTop} className="scroll-mt-4" />
      <Pager {...pager} />
      <Problem error={adjustments.error} />
      {adjustments.isSuccess && rows.length === 0 && <Note>No support adjustments</Note>}
      {rows.length > 0 && (
        <Table
          columns={[
            { label: "When" },
            { label: "Made by" },
            { label: "For" },
            { label: "Balance" },
            { label: "Credits", numeric: true },
            { label: "Reason" },
            { label: "Note" },
          ]}
          rows={rows.map(adjustment => ({
            key: adjustment.operationId,
            cells: [
              <When key="when" iso={adjustment.recordedAt} />,
              <PersonName key="by" person={adjustment.granter} />,
              <PersonName key="for" person={adjustment.recipient} />,
              bucketLabels[adjustment.bucket],
              <Credits key="credits" raw={adjustment.amountRaw} signed toned />,
              supportReasonLabels[adjustment.reason],
              adjustment.note,
            ],
          }))}
        />
      )}
      <Pager {...pager} scrollTo={listTop} />
    </div>
  );
}
